import React from "react";
import buildAdminNavigation from "./adminNavigation";
import applicationName from "./applicationName";
import t from "./translate";

export default function AdminSidebar({
  currentView,
  workspaceSchool,
  workspaceSection,
  branding,
  currentUser,
  collapsed = false,
  onToggleCollapse
}) {
  const resolvedApplicationName = applicationName();
  const logoUrl = branding?.logoUrl || window.SchoolBridge?.branding?.logoUrl || "";
  const faviconUrl = branding?.faviconUrl || window.SchoolBridge?.branding?.faviconUrl || "";
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
  const adminNavigation = buildAdminNavigation({ workspaceSchool });
  const workspacePathPrefix = workspaceSchool ? `/admin/schools/${workspaceSchool.id}/workspace` : "";
  const insideWorkspacePath = workspacePathPrefix ? currentPath.startsWith(workspacePathPrefix) : false;
  const canExitWorkspace = Boolean(currentUser?.platformUser && workspaceSchool);

  return (
    <aside className={`relative z-[180] flex flex-col border-r border-white/5 bg-[#0d1728] text-white transition-all duration-300 ${collapsed ? "w-28" : "w-80"}`}>
      <div className="px-4 pb-5 pt-6">
          <div className={`flex items-center justify-between rounded-[28px] bg-white/5 px-4 py-3 ${collapsed ? "min-h-[88px] flex-col gap-3 px-3" : ""}`}>
            <div className={`min-w-0 ${collapsed ? "flex items-center justify-center" : "flex-1"}`}>
            {collapsed ? (
              faviconUrl ? (
                <img src={faviconUrl} alt={resolvedApplicationName} className="h-12 w-12 rounded-2xl object-cover" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-sm font-semibold text-white">
                  {resolvedApplicationName.slice(0, 1).toUpperCase()}
                </div>
              )
            ) : logoUrl ? (
              <img src={logoUrl} alt={resolvedApplicationName} className="h-auto w-full max-w-[210px] object-contain" />
            ) : (
              <div className="text-lg font-semibold text-white">{resolvedApplicationName}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onToggleCollapse}
            className="relative z-[190] flex h-10 w-10 items-center justify-center rounded-2xl bg-white/5 text-white/65 transition hover:bg-white/10 hover:text-white"
            aria-label={collapsed ? t("shared.admin_sidebar.expand") : t("shared.admin_sidebar.collapse")}
          >
            <i className="fa-solid fa-bars-staggered" />
          </button>
        </div>

        {!collapsed ? (
          <div className="mt-5 rounded-[30px] bg-white/5 p-4">
            <div className="flex items-center justify-between rounded-2xl bg-[#13213a] px-3 py-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.24em] text-white/40">{t("shared.admin_sidebar.workspace")}</p>
                <p className="mt-1 text-sm font-semibold text-white">
                  {workspaceSchool?.name || t("shared.admin_sidebar.core_operations")}
                </p>
              </div>
              {canExitWorkspace ? (
                <a
                  href="/admin"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white/75 transition hover:bg-white/15 hover:text-white"
                  title={t("shared.admin_sidebar.exit_workspace")}
                  aria-label={t("shared.admin_sidebar.exit_workspace")}
                >
                  <i className="fa-solid fa-arrow-right-from-bracket text-sm" aria-hidden="true" />
                </a>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-visible px-4 pb-4">
        {adminNavigation.map((group, index) => (
          <div key={group.section} className={index === 0 ? "relative rounded-[32px] bg-white/5 p-3" : "relative mt-4 rounded-[32px] bg-white/5 p-3"}>
            {!collapsed ? <p className="px-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-white/30">{group.section}</p> : null}
            <ul className={collapsed ? "space-y-2" : "mt-3 space-y-1.5"}>
              {group.items.map((item) => {
                const active = currentPath === item.href ||
                  (item.matchPath && currentPath.startsWith(item.matchPath)) ||
                  item.view === currentView ||
                  (insideWorkspacePath && item.sectionKey === workspaceSection);

                return (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className={`group relative flex items-center rounded-2xl text-sm font-semibold transition ${
                        active
                          ? "bg-white text-slate-950 shadow-xl shadow-black/10"
                          : "text-white/68 hover:bg-white/7 hover:text-white"
                      } ${collapsed ? "justify-center px-2 py-3" : "gap-3 px-4 py-3"}`}
                    >
                      <span
                        className={`flex h-10 w-10 items-center justify-center rounded-2xl transition ${
                          active
                            ? "bg-brand-50 text-brand-700"
                            : "bg-white/5 text-white/55 group-hover:bg-white/10 group-hover:text-white"
                        }`}
                      >
                        <i className={`fa-solid ${item.icon} text-sm`} />
                      </span>
                      {!collapsed ? <span className="flex-1">{item.label}</span> : null}
                      {!collapsed && active ? <span className="h-2.5 w-2.5 rounded-full bg-accent-500" /> : null}
                      {collapsed ? (
                        <span className="pointer-events-none absolute left-full top-1/2 z-40 ml-3 -translate-y-1/2 whitespace-nowrap rounded-xl bg-slate-950 px-3 py-2 text-xs font-semibold text-white opacity-0 shadow-[0_16px_40px_rgba(15,23,42,0.28)] transition group-hover:opacity-100">
                          {item.label}
                        </span>
                      ) : null}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="mt-4 rounded-[30px] bg-brand-hero p-5 shadow-hero">
          {!collapsed ? (
            <>
              <p className="text-[11px] uppercase tracking-[0.28em] text-white/60">
                {workspaceSchool ? t("shared.admin_sidebar.selected_school") : t("shared.admin_sidebar.current_focus")}
              </p>
              <p className="mt-3 text-lg font-semibold">
                {workspaceSchool ? workspaceSchool.name : t("shared.admin_sidebar.tenant_operations")}
              </p>
              <p className="mt-2 text-sm leading-6 text-white/78">
                {workspaceSchool
                  ? t("shared.admin_sidebar.selected_school_description")
                  : t("shared.admin_sidebar.current_focus_description")}
              </p>
              <div className="mt-4 flex items-center justify-between rounded-2xl bg-white/12 px-4 py-3">
                <div>
                  <p className="text-xs text-white/60">{workspaceSchool ? t("shared.admin_sidebar.tenant") : t("shared.admin_sidebar.pending_tasks")}</p>
                  <p className="mt-1 text-base font-semibold">
                    {workspaceSchool ? workspaceSchool.tenantName : t("shared.admin_sidebar.actions_open")}
                  </p>
                </div>
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white">
                  {workspaceSchool ? t("shared.admin_sidebar.workspace_badge") : t("shared.admin_sidebar.live_badge")}
                </span>
              </div>
            </>
          ) : (
            <div className="flex justify-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white">
                <i className={`fa-solid ${workspaceSchool ? "fa-school" : "fa-layer-group"}`} />
              </span>
            </div>
          )}
        </div>
      </nav>
    </aside>
  );
}
